// Recurring tasks modal (mockups-feature-builds-2026-07 §2b) — a definitions
// list plus one inline add/edit form, opened from Tasks' "↻ Recurring"
// toolbar button. A recurrence generates one real project-less task each
// time it's due (taskRecurrenceRunner, nightly); this modal only manages the
// definitions, never the tasks it has already produced.
//
// Pause/resume is a first-class PATCH state: paused definitions remain listed
// and persist indefinitely, but the nightly runner skips them. Delete remains
// a separate soft-delete action whose rows move to Trash and eventually purge.

import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { anchorOptions, cadencePillText, defaultAnchorForCadence } from '../lib/recurrenceLabels';
import type { RecurrenceCadence } from '../../shared/recurrence';
import type { TaskRecurrenceRow } from '../../shared/types';
import { NOTES_MAX_LENGTH } from '../../shared/taskNotes';
import { formatDayMonth, formatWeekdayDate, Modal, Seg, SelectField, TrashAction } from './primitives';

import { useIsMobile } from '../lib/useIsMobile';
import { Sheet, SheetButton, SheetRow } from './Sheet';
import { PickerSheet } from './PickerSheet';
import { PageMenu } from './PageMenu';

interface RecurrenceForm {
  title: string;
  /** Copied onto every generated task (§7) — the checklist comes back
      unticked each cycle. */
  notes: string;
  cadence: RecurrenceCadence;
  anchor: string;
  priority: 'normal' | 'high';
}

function emptyForm(): RecurrenceForm {
  return { title: '', notes: '', cadence: 'weekly', anchor: 'mon', priority: 'normal' };
}

function formFrom(r: TaskRecurrenceRow): RecurrenceForm {
  return {
    title: r.title,
    notes: r.notes ?? '',
    cadence: r.cadence,
    anchor: r.anchor,
    priority: r.priority === 1 ? 'high' : 'normal',
  };
}

function nextDueLabel(r: TaskRecurrenceRow): string {
  return r.cadence === 'weekly' ? formatWeekdayDate(r.next_due) : formatDayMonth(r.next_due);
}

export function RecurringTasksModal({ onClose }: { onClose: () => void }) {
  const isMobile = useIsMobile();
  const [formOpen, setFormOpen] = useState(false);
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [recurrences, setRecurrences] = useState<TaskRecurrenceRow[] | null>(null);
  const [editing, setEditing] = useState<TaskRecurrenceRow | null>(null);
  const [form, setForm] = useState<RecurrenceForm>(emptyForm());
  // Collapsed by default so the add form stays one glance tall.
  const [notesOpen, setNotesOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api<{ task_recurrences: TaskRecurrenceRow[] }>('/api/admin/task-recurrences')
      .then((d) => setRecurrences(d.task_recurrences))
      .catch(() => setRecurrences([]));
  }, []);
  useEffect(refresh, [refresh]);

  const startEdit = (r: TaskRecurrenceRow) => {
    setEditing(r);
    setForm(formFrom(r));
    setNotesOpen(r.notes !== null && r.notes !== '');
    setError(null);
  };

  const cancelEdit = () => {
    setFormOpen(false);
    setAnchorOpen(false);
    setDeleteOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setNotesOpen(false);
    setError(null);
  };

  const setCadence = (cadence: RecurrenceCadence) => {
    setForm((f) => ({ ...f, cadence, anchor: defaultAnchorForCadence(cadence) }));
  };

  const submit = async () => {
    if (!form.title.trim() || busy) return;
    setBusy(true);
    setError(null);
    const json = {
      title: form.title.trim(),
      notes: form.notes.trim() === '' ? null : form.notes,
      cadence: form.cadence,
      anchor: form.anchor,
      priority: form.priority === 'high' ? 1 : 2,
    };
    try {
      if (editing) {
        await api(`/api/admin/task-recurrences/${editing.id}`, { method: 'PATCH', json });
      } else {
        await api('/api/admin/task-recurrences', { method: 'POST', json });
      }
      cancelEdit();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save recurrence.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await api(`/api/admin/task-recurrences/${id}`, { method: 'DELETE' })
      .catch((error) => reportError(error, 'Could not delete the recurrence.'));
    if (editing?.id === id) cancelEdit();
    refresh();
  };

  const setPaused = async (id: string, paused: boolean) => {
    await api(`/api/admin/task-recurrences/${id}`, { method: 'PATCH', json: { paused } })
      .catch((error) => reportError(error, 'Could not update the recurrence.'));
    refresh();
  };

  const count = recurrences?.length ?? 0;

  if (isMobile) {
    const current = recurrences?.find(r => r.id === editing?.id) ?? editing;
    const paused = current?.paused_at != null;
    return <>
      <Sheet open variant="full" title="Recurring tasks" context={`${count} recurrence${count === 1 ? '' : 's'} · generated daily`}
        rightAction={<SheetButton onClick={onClose}>Close</SheetButton>} onClose={onClose}
        footer={<button type="button" className="cbtn wide pri" onClick={() => { cancelEdit(); setFormOpen(true); }}>+ Add recurrence</button>}>
        {recurrences === null ? <p className="empty-note">Loading…</p> : recurrences.length === 0 ? <p className="empty-note">No recurring tasks yet.</p> :
          recurrences.map(r => <div key={r.id} className="tasks-recurrence-row" style={r.paused_at !== null ? { opacity: .45 } : undefined}>
            <SheetRow label={r.title} sub={<span className="tasks-recurrence-meta">
              <span className="pill">{cadencePillText(r.cadence, r.anchor)}</span>
              <span> · </span>{r.paused_at !== null ? <span className="pill gold">Paused</span> : <span>next {formatWeekdayDate(r.next_due)}</span>}
            </span>} right={<span aria-hidden="true">›</span>} onSelect={() => { startEdit(r); setFormOpen(true); }} />
          </div>)}
      </Sheet>
      {/* Back / dismiss are held while a save is in flight: submit() ends
          in cancelEdit(), which would otherwise clear a draft opened
          meanwhile (review). */}
      <Sheet open={formOpen} variant="full" title={editing ? 'Edit recurrence' : 'New recurrence'}
        onClose={() => { if (!busy) cancelEdit(); }}
        leftAction={<SheetButton disabled={busy} onClick={cancelEdit}>Back</SheetButton>}
        rightAction={<SheetButton tone="accent" disabled={!form.title.trim() || busy} onClick={() => void submit()}>Save</SheetButton>}
        footer={<button className="cbtn wide pri" disabled={!form.title.trim() || busy} onClick={() => void submit()}>Save recurrence →</button>}>
        <div className="tasks-recurrence-form">
          <label className="f-field"><span className="f-label">Title</span>
            <input className="f-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></label>
          <div className="f-field"><span className="f-label">Cadence</span>
            <Seg options={[{ value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }]} value={form.cadence} onChange={setCadence} /></div>
          <div className="f-field"><span className="f-label">Anchor</span>
            {form.cadence === 'weekly' ? <div className="chiprow tasks-anchor-chips">{anchorOptions('weekly').map(a =>
              <button key={a.value} type="button" className={`c${form.anchor === a.value ? ' on' : ''}`} aria-pressed={form.anchor === a.value}
                onClick={() => setForm(f => ({ ...f, anchor: a.value }))}>{a.label}</button>)}</div> :
              <button type="button" className="fsheet-trigger" onClick={() => setAnchorOpen(true)}>
                <span>{anchorOptions('monthly').find(a => a.value === form.anchor)?.label}</span><span>▾</span></button>}
          </div>
          <div className="f-field"><span className="f-label">Priority</span>
            <Seg options={[{ value: 'normal', label: 'Normal' }, { value: 'high', label: 'High ⚑' }]} value={form.priority} onChange={priority => setForm(f => ({ ...f, priority }))} /></div>
          <label className="f-field"><span className="f-label">Notes · copied onto every task</span>
            <textarea className="f-input notes-textarea" maxLength={NOTES_MAX_LENGTH} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></label>
          {error && <p className="error-text">{error}</p>}
        </div>
        {editing && <>
          <SheetRow label={paused ? 'Paused' : 'Active'} sub={paused ? 'Resume to generate again' : 'Pause to stop generating'}
            right={<span className="cbtn acc">{paused ? 'Resume' : 'Pause'}</span>} onSelect={() => void setPaused(editing.id, !paused)} />
          <SheetRow label="Delete recurrence" tone="danger" onSelect={() => setDeleteOpen(true)} />
        </>}
      </Sheet>
      <PickerSheet open={formOpen && anchorOpen} title="Monthly anchor" items={anchorOptions('monthly')} value={form.anchor}
        onSelect={anchor => setForm(f => ({ ...f, anchor }))} onClose={() => setAnchorOpen(false)} />
      <PageMenu open={formOpen && deleteOpen} title="Delete recurrence?" onClose={() => setDeleteOpen(false)}
        items={[{ key: 'delete', label: 'Delete', tone: 'danger', onSelect: () => { if (editing) void remove(editing.id); } }]} />
    </>;
  }

  return (
    <Modal
      title="Recurring tasks"
      context="TASKS"
      width={580}
      onClose={onClose}
      hint={`${count} recurrence${count === 1 ? '' : 's'} · generated daily`}
      foot={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      {recurrences === null ? (
        <p className="empty-note">Loading…</p>
      ) : (
        <>
          {recurrences.length === 0 && <p className="empty-note">No recurring tasks yet.</p>}
          {recurrences.map((r) => {
            const paused = r.paused_at !== null;
            return (
              <div className="rec-row" key={r.id} style={paused ? { opacity: 0.35 } : undefined}>
                <div className="rec-main">
                  <span className="rec-title" title={r.title}>
                    {r.title}
                  </span>
                  <span className="pill">{cadencePillText(r.cadence, r.anchor)}</span>
                  {paused && <span className="pill">PAUSED</span>}
                </div>
                <span className="rec-next">{nextDueLabel(r)}</span>
                <div className="rec-actions">
                  <a className="group-link" onClick={() => startEdit(r)} style={{ cursor: 'pointer' }}>
                    Edit
                  </a>
                  <a
                    className="group-link"
                    onClick={() => setPaused(r.id, !paused)}
                    style={{ cursor: 'pointer' }}
                  >
                    {paused ? 'Resume' : 'Pause'}
                  </a>
                  <TrashAction label="Delete" onConfirm={() => remove(r.id)} />
                </div>
              </div>
            );
          })}
        </>
      )}

      <div className="rec-add">
        <div className="f-field">
          <span className="f-label">Title</span>
          <input
            className="f-input"
            value={form.title}
            placeholder="e.g. Update client dashboards…"
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
        </div>
        <div className="f-field">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="f-label">Notes</span>
            <button
              type="button"
              className="btn"
              title="Notes copied onto every task this recurrence generates"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--fs-eyebrow)',
                color: form.notes ? 'var(--accent)' : 'var(--text-faint)',
              }}
              onClick={() => setNotesOpen((o) => !o)}
            >
              {notesOpen ? 'hide' : form.notes ? 'notes' : 'add'}
            </button>
          </div>
          {notesOpen && (
            <textarea
              className="f-input notes-textarea"
              style={{ minHeight: 96 }}
              maxLength={NOTES_MAX_LENGTH}
              value={form.notes}
              placeholder={'## Kit\n- [ ] Batteries charged'}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="f-field" style={{ flex: 1 }}>
            <span className="f-label">Cadence</span>
            <Seg
              options={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              value={form.cadence}
              onChange={setCadence}
            />
          </div>
          <div className="f-field" style={{ flex: 1 }}>
            <span className="f-label">Anchor</span>
            <SelectField
              items={anchorOptions(form.cadence)}
              value={form.anchor}
              onChange={(anchor) => setForm((f) => ({ ...f, anchor }))}
            />
          </div>
          <div className="f-field" style={{ flex: 1 }}>
            <span className="f-label">Priority</span>
            <Seg
              options={[
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High ⚑' },
              ]}
              value={form.priority}
              onChange={(priority) => setForm((f) => ({ ...f, priority }))}
            />
          </div>
        </div>
        {error && <p className="error-text">{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn primary"
            onClick={submit}
            disabled={!form.title.trim() || busy}
          >
            {editing ? 'Save changes →' : '+ Add recurrence'}
          </button>
          {editing && (
            <button type="button" className="btn" onClick={cancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
